<template>
  <div
    class="rs-textarea"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup>
      <RsListingScoreHeart
        :show-heart="showHeart"
      />
    </label>
    <ValidationProvider
      v-slot="{ errors }"
      class="veeValidate"
      :name="label ? label : name ? name : 'field'"
      mode="aggressive"
      :rules="rules"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>This field is managed by <strong>[Data Source]</strong>.<br>Changing this will be temporary.</p>
      </div>
      <BInputGroup>
        <BInputGroupPrepend
          v-if="imported"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon
              :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'"
            />
          </span>
        </BInputGroupPrepend>
        <BFormTextarea
          :id="id"
          :disabled="disabled || (imported && isUnlocked == false)"
          :value="htmlDecode ? decodeHtml(`${(value || '')}`) : value"
          :rows="rows"
          :max-rows="maxRows"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :no-resize="!allowResize"
          :state="errors[0] ? false : valid ? true : null"
          @input="changeInput"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
        />
      </BInputGroup>
      <div
        v-if="errors.length"
        class="text--error"
      >
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </div>
</template>
<script lang="ts">
import { ValidationProvider, extend } from 'vee-validate';
import { defineComponent } from 'vue';
import { methods } from '@/mixins/validation-mixin';
import { decodeHtml, encodeHtml } from '@/shared';
import { embedCodeMsg, isEmbedCode } from '@/shared/helpers/is-embed-code';

const multiEmailValidate = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return true;
  }

  const valueSet = (value ?? '').split('\n'); // value.replace(/\s/g, '');
  for (let i = 0; i < valueSet.length; i++) {
    if (valueSet[i] !== '' && !methods.isEmail(valueSet[i])) {
      return false;
    }
  }
  return true;
};
extend('multiEmail', {
  validate: multiEmailValidate,
  message: 'Emails are not valid.',
});

extend('multiEmailAlt', {
  validate: multiEmailValidate,
  message: 'Input is not valid email address(es).',
});

extend('embedCode', {
  validate: (value) => {
    const trimValue = (value ?? '').replace(/\s/g, '');

    if (trimValue === '') return true;
    return isEmbedCode(trimValue);
  },
  message: embedCodeMsg,
});

extend('validJson', {
  validate: (value) => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return true;
    try {
      JSON.parse(trimmed);
      return true;
    } catch {
      return false;
    }
  },
  message: 'Invalid JSON format.',
});

export default defineComponent({
  name: 'RsFormTextarea',
  components: {
    ValidationProvider,
  },

  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    alt: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [String, Number],
      default: '3',
    },
    maxRows: {
      type: [String, Number],
      default: '8',
    },
    maxlength: [String, Number],
    value: {
      type: [String, Number],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowResize: {
      type: Boolean,
      default: true,
    },
    imported: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'input',
    },
    showHeart: {
      type: Boolean,
      default: false,
    },

    htmlDecode: {
      type: Boolean,
      default: false,
    },
    /**
     * Color variants:  success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop:string) => [
        'success',
        'warning',
        'error',
        'error-alt',
      ].includes(prop),
    },
  },
  data() {
    return {
      valid: null,
      isUnlocked: false,
      importHover: false,
    };
  },
  computed: {
    inputClasses(): Record<string, boolean> {
      return {
        'rs-textarea--success': this.variant === 'success',
        'rs-textarea--warning': this.variant === 'warning',
        'rs-textarea--error': this.variant === 'error',
        'rs-textarea--error-alt': this.variant === 'error-alt',
        'rs-textarea--alt': this.alt,
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
  },
  methods: {
    decodeHtml,
    encodeHtml,

    changeInput(e: Event): void {
      this.$emit('input', this.htmlDecode ? encodeHtml(`${(e || '')}`) : e);
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';
.rs-textarea {
  margin-bottom: 1rem;

  textarea {
    padding: $input-padding;
    width: 100%;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    white-space: pre-line;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }

  &--error-alt {
    textarea {
      border-color: $error;
      color: $error;
      background-color: rgba($error, 0.3);
    }
  }
}
</style>
